import { For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { GithubTokenConnection } from '../lib/github-token';
import { absolute, ago } from '../lib/time';
import { GithubTokenConnectForm } from './GithubTokenConnectForm';
import { Panel } from './Panel';
import { Button } from './ui/Button';

// The GitHub user-token connect card (#21). Presenter — dispatches on
// `connection`: null → the connect form; a live connection → its expiry + repos
// + an inline-confirmed Disconnect. All async state is handed down by the route.
export type GithubTokenCardProps = {
  readonly connection: GithubTokenConnection | null;
  // connect
  readonly connectBusy: boolean;
  readonly connectError: string | null;
  readonly onConnect: (input: { token: string; repos: string }) => void;
  // disconnect (inline two-step confirm)
  readonly confirming: boolean;
  readonly disconnectBusy: boolean;
  readonly disconnectError: string | null;
  readonly onAskDisconnect: () => void;
  readonly onCancelDisconnect: () => void;
  readonly onConfirmDisconnect: () => void;
};

export function GithubTokenCard(props: GithubTokenCardProps): JSX.Element {
  return (
    <Show
      when={props.connection}
      fallback={
        <Panel title={t('github_token.connect_title')} class="github-token">
          <p class="github-token__lead">{t('github_token.connect_lead')}</p>
          <GithubTokenConnectForm
            busy={props.connectBusy}
            error={props.connectError}
            onConnect={props.onConnect}
          />
        </Panel>
      }
    >
      {(conn) => (
        <Panel title={t('github_token.connected_title')} class="github-token">
          <p class="github-token__lead">{t('github_token.connected_lead')}</p>
          <dl class="facts facts--rows">
            <div>
              <dt>{t('github_token.expires_label')}</dt>
              <dd>
                <Show when={conn().expires_at} fallback={t('github_token.no_expiry')}>
                  {(exp) => (
                    <time datetime={exp()} title={absolute(exp())}>
                      {t('github_token.expires_value', {
                        relative: ago(exp()),
                        absolute: absolute(exp()),
                      })}
                    </time>
                  )}
                </Show>
              </dd>
            </div>
            <div>
              <dt>{t('github_token.repos_label')}</dt>
              <dd>
                <ul class="github-token__repos">
                  <For each={conn().repos}>
                    {(repo) => (
                      <li>
                        <code>{repo}</code>
                      </li>
                    )}
                  </For>
                </ul>
              </dd>
            </div>
          </dl>

          <Show when={props.disconnectError}>
            {(msg) => (
              <p class="field__error" role="alert">
                {msg()}
              </p>
            )}
          </Show>

          <div class="github-token__actions">
            <Show
              when={props.confirming}
              fallback={
                <Button variant="outline" size="sm" onClick={props.onAskDisconnect}>
                  {t('github_token.disconnect')}
                </Button>
              }
            >
              <p class="github-token__confirm-q">{t('github_token.disconnect_confirm')}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={props.onCancelDisconnect}
                disabled={props.disconnectBusy}
              >
                {t('github_token.cancel')}
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={props.onConfirmDisconnect}
                disabled={props.disconnectBusy}
              >
                {t(
                  props.disconnectBusy
                    ? 'github_token.disconnecting'
                    : 'github_token.disconnect_cta',
                )}
              </Button>
            </Show>
          </div>
        </Panel>
      )}
    </Show>
  );
}
